// PHA-1168 Reports: the standalone Reports page. Lists the saved reports for the
// selected organization, runs them, and lets designers create, edit and archive them.
// Reports are also runnable from Ask Phanova by `#handle`.
// PHA-1243: New report opens the builder directly (it picks the dataset itself), and this
// page lists only the reports that live here; billing reports made on /billing live there.
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Callout, Card, Flex, Text } from '@radix-ui/themes';
import { InfoCircledIcon, PlusIcon } from '@radix-ui/react-icons';
import ReportsLayout from './ReportsLayout';
import ReportList from './ReportList';
import { useSavedReports } from './useSavedReports';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useCustomViewsAccess } from '../customViews/useCustomViews';

const ReportsPage: React.FC = () => {
  const navigate = useNavigate();
  const { orgId, orgName } = useOrganization();
  const access = useCustomViewsAccess(orgId, 'report');
  // Always refetch on arrival: a report saved a minute ago must be listed.
  const saved = useSavedReports(orgId, { enabled: access.enabled, maxAgeMs: 0 });

  const visible = useMemo(
    () => saved.reports.filter((r) => (r.home ?? 'reports') === 'reports'),
    [saved.reports],
  );

  let body: React.ReactNode;
  if (!orgId) {
    body = (
      <Callout.Root>
        <Callout.Icon><InfoCircledIcon /></Callout.Icon>
        <Callout.Text>Pick a clinic to see its reports.</Callout.Text>
      </Callout.Root>
    );
  } else if (access.loading || (saved.loading && !saved.reports.length)) {
    body = <Text color="gray">Loading reports…</Text>;
  } else if (!access.enabled) {
    body = (
      <Callout.Root>
        <Callout.Icon><InfoCircledIcon /></Callout.Icon>
        <Callout.Text>Reports are not turned on for {orgName || 'this clinic'} yet. Contact Phanova support to enable them.</Callout.Text>
      </Callout.Root>
    );
  } else {
    body = (
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center" wrap="wrap" gap="2">
          <Text size="2" color="gray">
            {orgId === 'ALL'
              ? 'All organizations: reports run across every clinic.'
              : `Reports for ${orgName || 'this clinic'}.`}{' '}
            Type <Text weight="bold">#</Text> in Ask Phanova to run one.
          </Text>
          {access.canDesign && (
            <Button onClick={() => navigate('/reports/new')}><PlusIcon /> New report</Button>
          )}
        </Flex>
        <Card>
          <ReportList
            reports={visible}
            canDesign={access.canDesign}
            emptyText={`No saved reports yet.${access.canDesign ? ' Use "New report" to describe one in plain English.' : ''}`}
            onChanged={saved.refresh}
          />
        </Card>
      </Flex>
    );
  }

  return (
    <ReportsLayout
      title="Reports"
      subtitle="Saved reports built in plain English. Run them here, export them, or ask for them in Ask Phanova."
      backTo="/"
    >
      {body}
    </ReportsLayout>
  );
};

export default ReportsPage;
